import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Popover,
  Box,
  Button,
  CircularProgress,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Typography,
  TextField,
  Divider,
} from '@mui/material';
import { useAuthenticator } from '@aws-amplify/ui-react';
import {
  useGetSuggestedKeywordsLazyQuery,
  useSaveValidationKeywordMutation,
  useGetUserIdByAuthIdLazyQuery,
} from '../../gql/generated/graphql';

interface KeywordSelectionPopoverProps {
  companyId: number;
  validationType: 'software' | 'not_software';
  onConfirm: (keyword?: string) => void;
  children: React.ReactElement;
}

const KeywordSelectionPopover = ({ companyId, validationType, onConfirm, children }: KeywordSelectionPopoverProps) => {
  const { t } = useTranslation();
  const { user } = useAuthenticator();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedKeyword, setSelectedKeyword] = useState<string>('');
  const [customKeyword, setCustomKeyword] = useState<string>('');
  const [isConfirming, setIsConfirming] = useState(false);

  const [getSuggestedKeywords, { data: keywordsData, loading: keywordsLoading }] = useGetSuggestedKeywordsLazyQuery();
  const [getUserIdByAuthId, { data: userIdData }] = useGetUserIdByAuthIdLazyQuery();
  const [saveValidationKeyword] = useSaveValidationKeywordMutation();

  // Get userId when component mounts
  useEffect(() => {
    if (user?.signInDetails?.loginId) {
      getUserIdByAuthId({
        variables: { authId: user.signInDetails.loginId },
      });
    }
  }, [user, getUserIdByAuthId]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    // Start loading keywords immediately
    getSuggestedKeywords({
      variables: { companyId },
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedKeyword('');
    setCustomKeyword('');
    setIsConfirming(false);
  };

  const handleConfirmWithoutKeyword = async () => {
    setIsConfirming(true);
    try {
      await onConfirm();
    } finally {
      handleClose();
    }
  };

  const handleConfirmWithKeyword = async () => {
    setIsConfirming(true);
    try {
      // Prioritize custom keyword over dropdown selection
      const finalKeyword = customKeyword.trim() || selectedKeyword.trim();
      const keywordSource = customKeyword.trim() ? 'custom' : 'manual_selection';

      // Save keyword if one is provided
      if (finalKeyword) {
        const userId = userIdData?.userIdByAuthId;
        if (userId) {
          await saveValidationKeyword({
            variables: {
              companyId,
              userId,
              keyword: finalKeyword,
              source: keywordSource,
              isSoftware: validationType === 'software',
            },
          });
        }
      }
      await onConfirm(finalKeyword);
    } finally {
      handleClose();
    }
  };

  const open = Boolean(anchorEl);

  // Extract keywords based on validation type
  const keywordObjects =
    validationType === 'software'
      ? keywordsData?.getSuggestedKeywords?.software || []
      : keywordsData?.getSuggestedKeywords?.nonSoftware || [];

  // Extract just the keyword strings from the objects
  const keywords = keywordObjects.map(kw => kw?.keyword || '').filter(k => k);

  // Clone the child element and add onClick handler
  const childWithClick = children
    ? React.cloneElement(children, {
        onClick: (e: React.MouseEvent<HTMLElement>) => {
          handleClick(e);
          // Call original onClick if it exists
          if (children.props.onClick) {
            children.props.onClick(e);
          }
        },
      })
    : null;

  return (
    <>
      {childWithClick}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300, maxWidth: 400 }}>
          <Typography variant="subtitle2" gutterBottom>
            {validationType === 'software'
              ? t('validation.keywordSelection.reasonForSoftware')
              : t('validation.keywordSelection.reasonForNotSoftware')}
          </Typography>

          <FormControl fullWidth size="small" sx={{ mb: 2, mt: 1 }}>
            <InputLabel id="keyword-select-label">
              {keywordsLoading
                ? t('validation.keywordSelection.loading')
                : t('validation.keywordSelection.selectReason')}
            </InputLabel>
            <Select
              labelId="keyword-select-label"
              value={selectedKeyword}
              onChange={e => setSelectedKeyword(e.target.value)}
              label={
                keywordsLoading
                  ? t('validation.keywordSelection.loading')
                  : t('validation.keywordSelection.selectReason')
              }
              disabled={keywordsLoading || isConfirming}
              endAdornment={keywordsLoading ? <CircularProgress size={20} sx={{ mr: 2 }} /> : null}
            >
              {keywords.map(keyword => (
                <MenuItem key={keyword} value={keyword}>
                  {keyword}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider sx={{ my: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {t('validation.keywordSelection.orEnterCustom', 'or enter your own')}
            </Typography>
          </Divider>

          <TextField
            fullWidth
            size="small"
            placeholder={t('validation.keywordSelection.customKeywordPlaceholder', 'Enter custom reason...')}
            value={customKeyword}
            onChange={e => setCustomKeyword(e.target.value)}
            disabled={isConfirming}
            sx={{ mb: 2 }}
          />

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button size="small" onClick={handleConfirmWithoutKeyword} disabled={isConfirming} variant="outlined">
              {t('validation.keywordSelection.skipReason')}
            </Button>
            <Button
              size="small"
              onClick={handleConfirmWithKeyword}
              disabled={isConfirming || (!selectedKeyword && !customKeyword && !keywordsLoading)}
              variant="contained"
              color="primary"
            >
              {isConfirming ? <CircularProgress size={20} /> : t('validation.keywordSelection.confirm')}
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default KeywordSelectionPopover;
